/**
 * Pure decision for useCallMode's silence-WARNING retraction.
 *
 * Every MIC_SILENCE WARNING useCallMode shows is a claim that "your mic
 * hears nothing" — the ~15s no-speech nudge, switchMic's onSilent, and the
 * silence-watchdog advisory. A speech-level peak in the capture pump
 * falsifies that claim for ALL of them at once, so retracting "whichever
 * MIC_SILENCE WARNING happens to be showing" needs no per-call-site
 * dismissal wiring — just this one rule, applied at the exact edge speech
 * is first detected (see useCallMode.ts's `speechSeen` flip in the
 * workletNode.port.onmessage pump).
 *
 * The retraction keys on `topic`, not on kind alone: a CONNECTION WARNING
 * ("Connection lost — reconnecting…", FB28) is also a WARNING, but the user
 * talking into a live mic while the socket is down proves nothing about the
 * network — that notice is cleared by its own restore/stop paths instead.
 * Same reasoning applies to MODEL_BUSY (rate_limit_wait, FB32): speech
 * proves nothing about whether OpenAI is still rate-limiting responses, so
 * this function must never touch it either — see retractModelBusyNotice
 * below for its own, differently-keyed retraction.
 *
 * SUCCESS and ERROR are deliberately untouched: SUCCESS auto-dismisses on
 * its own timer (see SUCCESS_NOTICE_MS in useCallMode.ts), and ERROR is a
 * real failure that "the user is being heard" says nothing about.
 */

import { CallNoticeKind, CallNoticeTopic, type CallNotice } from '@/types/chat';

export function retractSilenceWarning(
  notice: CallNotice | null
): CallNotice | null {
  return notice?.kind === CallNoticeKind.WARNING &&
    notice.topic === CallNoticeTopic.MIC_SILENCE
    ? null
    : notice;
}

// Turn 15 owner-locked copy — exact strings, do not edit without re-checking
// the design brief (Claude Design Turn 15, 15b/15d).
const PATIENCE_TITLE = "Sara's gathering her thoughts — one moment.";
const PATIENCE_SUBTITLE = 'Hang tight, her reply is on its way.';

/**
 * Turn 15's "patience notice" (15b) — the calm WAITING treatment for a
 * scheduled rate_limit_wait retry (retry_in non-null). The backend keeps
 * sending its normal turn_state:"thinking" frame alongside rate_limit_wait,
 * so the orb shimmer / "Working…" timeline already stays alive on its own —
 * this notice exists purely to give the honest reason for the pause, without
 * the amber "something needs fixing" temperature WARNING carries. No
 * countdown/ETA is rendered even though `retry_in` is available — the design
 * is explicit that this is an INDETERMINATE wait, not a progress bar (see
 * call-notice.tsx's sweep track).
 */
export function patienceNotice(): CallNotice {
  return {
    kind: CallNoticeKind.PATIENCE,
    topic: CallNoticeTopic.MODEL_BUSY,
    title: PATIENCE_TITLE,
    subtitle: PATIENCE_SUBTITLE,
  };
}

/**
 * Turn 15's EXHAUSTED treatment (15d) — the retry budget ran out and the
 * backend has stopped retrying on its own; the ONLY way forward is the
 * user tapping "Ask again" (manual retry — `onAskAgain` sends the
 * RETRY_RESPONSE frame, see useCallMode.askAgain). Kept as a WARNING, never
 * ERROR/red: red is reserved for true failures elsewhere in this family,
 * and a rate-limit pause the user can immediately retry from isn't one.
 */
export function exhaustedNotice(onAskAgain: () => void): CallNotice {
  return {
    kind: CallNoticeKind.WARNING,
    topic: CallNoticeTopic.MODEL_BUSY,
    title: "Sara's having trouble responding right now.",
    subtitle: "Ask again and she'll pick right back up.",
    action: { label: 'Ask again', onClick: onAskAgain },
  };
}

/**
 * Builds the MODEL_BUSY notice for a rate_limit_wait frame (FB32, Turn 15).
 * `retryIn` non-null → a retry is scheduled → the calm PATIENCE notice;
 * `null` → the retry budget is exhausted → the amber "Ask again" WARNING.
 * `onAskAgain` is only ever invoked via the exhausted branch's action.
 */
export function modelBusyNotice(
  retryIn: number | null,
  onAskAgain: () => void
): CallNotice {
  return retryIn != null ? patienceNotice() : exhaustedNotice(onAskAgain);
}

/**
 * Pure mapping from a rate_limit_wait frame to useCallMode's `modelBusy`
 * state (Turn 15) — the thing that drives the orb's dim-and-rest treatment
 * and the status cluster's "Resting a moment" override, independent of
 * whatever the notice itself renders. Split out from modelBusyNotice so the
 * STATE decision (what useCallMode remembers between renders) is testable
 * on its own; note the "Ask again" tap changes this state WITHOUT going
 * through this mapper (it's a direct 'waiting' assignment — see
 * useCallMode.askAgain), since there's no fresh rate_limit_wait frame at
 * that moment to map from.
 */
export type ModelBusyState = 'waiting' | 'exhausted';
export function nextModelBusyState(retryIn: number | null): ModelBusyState {
  return retryIn != null ? 'waiting' : 'exhausted';
}

/**
 * Pure decision for useCallMode's MODEL_BUSY retraction. Unlike
 * retractSilenceWarning, this keys on `topic` ALONE (not kind): MODEL_BUSY
 * carries PATIENCE (retry scheduled, Turn 15) or WARNING (retries
 * exhausted) — there's no SUCCESS variant of it to protect from over-eager
 * clearing — so any notice on this topic is stale the moment Sara's output
 * actually starts (see the turn_state:"responding" hook in
 * useAssistantConnection.ts that calls this). Also used, unmodified, as the
 * MATCH CHECK for Turn 15's "the exhale" recovery animation — see
 * useCallMode.handleModelResponded, which snapshots the notice this would
 * have nulled instead of clearing it instantly.
 */
export function retractModelBusyNotice(
  notice: CallNotice | null
): CallNotice | null {
  return notice?.topic === CallNoticeTopic.MODEL_BUSY ? null : notice;
}

/**
 * Pure placement decision for CallScreen's in-call notice — extracted out of
 * the inline `isSuccessNotice`/`isModelBusyNotice`/`usesSharedNoticeSlot`/
 * `controlsNotice`/`topNotice` derivation chain that used to live there.
 *
 * `null` → nothing to show. `'ribbon'` → Turn 15's mobile-only MODEL_BUSY
 * ribbon (15f), flush under the top bar — never the shared controls/top
 * slots (desktop keeps MODEL_BUSY in `'controls'` alongside every other
 * WARNING/PATIENCE; see model-busy-ribbon.tsx's header for why mobile gets
 * its own presentation). Otherwise: desktop puts everything in `'controls'`
 * (above the call controls) except SUCCESS, which reads near the transcript
 * (`'top'`); mobile puts only WARNING in `'controls'` (just above the
 * controls, where the fix — e.g. the mic switcher — lives) and everything
 * else in `'top'` (dropping from the very top, under the status bar).
 */
export type NoticeSlot = 'controls' | 'top' | 'ribbon';

export function resolveNoticeSlot(
  notice: CallNotice | null,
  isDesktop: boolean
): NoticeSlot | null {
  if (!notice) return null;
  if (notice.topic === CallNoticeTopic.MODEL_BUSY && !isDesktop) {
    return 'ribbon';
  }
  if (isDesktop) {
    return notice.kind === CallNoticeKind.SUCCESS ? 'top' : 'controls';
  }
  return notice.kind === CallNoticeKind.WARNING ? 'controls' : 'top';
}
